import { Form, router } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { type ReactElement, useState } from "react"
import { useTranslation } from "react-i18next"

import { BadgeImage } from "@/components/badges/badge-image"
import { PremiumBadge } from "@/components/badges/premium-badge"
import { BrandImageUploader } from "@/components/brand-image-uploader"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { tierFor } from "@/lib/badge-tiers"
import { milestoneBadgeImagePath } from "@/routes"
import type { MilestoneCatalogTile } from "@/types/badges"

const IMAGE_ACCEPT = "image/png,image/webp"
const IMAGE_MAX_BYTES = 2 * 1024 * 1024
const IMAGE_MIN_DIMENSION = 128

/**
 * OKS-573 — Per-brand milestone-badge image override dialog.
 *
 * The image-only sibling of `<CustomBadgeFormDialog>`: milestone name,
 * meaning, icon and unlock rule are platform-defined and shown READ-ONLY;
 * the only editable surface is a `<BrandImageUploader>` that overrides the
 * shipped default artwork for THIS brand + milestone family.
 *
 * Crew rules: Inertia `<Form>` only — NO shadcn `Form`/RHF/zod. The file
 * input's `name="milestone_badge_override[image]"` maps to the Rails
 * strong-param; a file input in the form auto-triggers a multipart PATCH
 * to `milestoneBadgeImagePath(identifier)`. Server validation errors flow
 * back through the render-prop `errors.image`.
 *
 * # Save vs Remove (TRD KD-4, PO-approved)
 *
 * - Save → attach/replace via the PATCH `<Form>`.
 * - Remove → an IMMEDIATE `router.delete` to the same endpoint (reuses
 *   the shipped uploader's `onRemove`, idempotent 303) — not staged
 *   behind Save. The preview reverts to the Lucide default and every
 *   render surface refreshes on the server round-trip.
 *
 * # Frame-less vs medallion
 *
 * The uploader preview and all render surfaces show the override
 * frame-less (`<BadgeImage>`); the no-image default keeps the milestone's
 * Lucide `<PremiumBadge>` medallion — consistent with OKS-538 customs.
 */
interface MilestoneBadgeFormDialogProps {
  trigger: ReactElement
  /** The milestone catalogue tile (identifier, icon, accent, override). */
  tile: MilestoneCatalogTile
  /** Localized milestone name (platform-defined). */
  name: string
  /** Localized milestone description (platform-defined). */
  description: string
}

export function MilestoneBadgeFormDialog({
  trigger,
  tile,
  name,
  description,
}: MilestoneBadgeFormDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const action = milestoneBadgeImagePath(tile.identifier)
  const tier = tierFor({ kind: "milestone", identifier: tile.identifier })

  // Client pre-check — a fast inline hint mirroring the server rules
  // (PNG/WebP, ≤2 MB, square ≥128 px). The server stays authoritative.
  const validateImage = (file: File): Promise<string | null> => {
    if (!IMAGE_ACCEPT.split(",").includes(file.type)) {
      return Promise.resolve(t("badges.form.image.errors.type"))
    }
    if (file.size > IMAGE_MAX_BYTES) {
      return Promise.resolve(t("badges.form.image.errors.size"))
    }
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file)
      const probe = new window.Image()
      probe.onload = () => {
        URL.revokeObjectURL(url)
        const square = probe.naturalWidth === probe.naturalHeight
        const big = probe.naturalWidth >= IMAGE_MIN_DIMENSION
        resolve(square && big ? null : t("badges.form.image.errors.dimensions"))
      }
      probe.onerror = () => {
        URL.revokeObjectURL(url)
        resolve(t("badges.form.image.errors.dimensions"))
      }
      probe.src = url
    })
  }

  // Remove the SAVED override → immediate DELETE (idempotent). Purge +
  // redirect refreshes props so every surface reverts to the default.
  const onRemoveSaved = tile.image_url
    ? () => {
        router.delete(action, {
          preserveScroll: true,
          onSuccess: () => setOpen(false),
        })
      }
    : undefined

  // Preview: frame-less image when a URL is present, else the shipped
  // Lucide medallion default (the milestone's platform artwork).
  const renderPreview = (url: string | null) =>
    url ? (
      <BadgeImage
        imageUrl={url}
        accentColor={tile.accent_color}
        name={name}
        size="modal"
      />
    ) : (
      <PremiumBadge tier={tier} icon={tile.icon} state="earned" size="hero" />
    )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={trigger} />
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{name}</DialogTitle>
          {/* Platform-defined meaning, shown for context (read-only). */}
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Form
          action={action}
          method="patch"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
          data-testid="milestone-badge-form"
          className="flex flex-col gap-4"
        >
          {({ errors, processing }) => (
            <>
              <div className="grid gap-2">
                <BrandImageUploader
                  currentUrl={tile.image_url}
                  name="milestone_badge_override[image]"
                  label={t("badges.form.image.label")}
                  helper={t("badges.form.image.helper")}
                  removeLabel={t("badges.form.image.remove")}
                  accept={IMAGE_ACCEPT}
                  onRemove={onRemoveSaved}
                  error={errors.image}
                  validate={validateImage}
                  renderPreview={renderPreview}
                  testId="milestone-image-uploader"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                >
                  {t("badges.actions.cancel")}
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing && (
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {t("badges.milestones.save")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default MilestoneBadgeFormDialog
